import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, RefreshCcw, ArrowLeft, Mail, Building2, FileText, Boxes, Gauge, Clock, Download, Filter, X, FileDown } from "lucide-react";
import { BrandLockup } from "@/components/vivasvan/Logo";
import { Link } from "react-router-dom";
import { useT } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const { locale } = useT();
  const [key, setKey] = useState("");
  const [authed, setAuthed] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const buildParams = (k = key) => {
    const p = { admin_key: k };
    if (start) p.start = start;
    if (end) p.end = end;
    return p;
  };

  const load = async (k, params) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/trade-inquiries`, { params: params || buildParams(k) });
      setItems(res.data || []);
      setAuthed(true);
      toast.success("Ledger unlocked", { description: `${res.data?.length || 0} entries loaded.` });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        toast.error("Access denied", { description: "Invalid administrator key." });
        setAuthed(false);
      } else if (status === 400) {
        toast.error("Invalid filter", { description: err?.response?.data?.detail || "Check date range." });
      } else {
        toast.error("Failed to load", { description: err?.message || "Network error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const onUnlock = (e) => {
    e.preventDefault();
    if (!key) return;
    load(key, { admin_key: key });
  };

  const applyFilter = () => {
    if (!authed) return;
    load(key);
  };

  const clearFilter = () => {
    setStart("");
    setEnd("");
    load(key, { admin_key: key });
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/trade-inquiries/export`, {
        params: buildParams(),
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url;
      a.download = `vivasvan-ledger-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Ledger exported", { description: `${items.length} entries downloaded.` });
    } catch (err) {
      const status = err?.response?.status;
      toast.error("Export failed", { description: status === 401 ? "Session expired." : (err?.message || "Try again.") });
    }
  };

  const downloadCertificate = async (inq) => {
    try {
      const res = await axios.get(`${API}/trade-inquiries/${inq.id}/certificate`, {
        params: { admin_key: key, lang: locale },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const slug = (inq.company_name || "certificate").replace(/\s+/g, "-").slice(0, 40);
      a.href = url;
      a.download = `vivasvan-certificate-${slug}-${locale}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Certificate generated", { description: `${inq.company_name} · ${locale.toUpperCase()} PDF` });
    } catch (err) {
      const status = err?.response?.status;
      toast.error("Certificate failed", { description: status === 401 ? "Session expired." : (err?.message || "Try again.") });
    }
  };

  const inputClass =
    "w-full bg-[#020C1B] border border-[#94A3B8]/25 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] text-bone font-sans text-[15px] px-4 py-3 rounded-none transition-colors";

  const dateClass =
    "bg-[#020C1B] border border-[#94A3B8]/25 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] text-bone font-sans text-[13px] px-3 py-2 rounded-none transition-colors";

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#0A192F] text-bone">
      <header className="border-b border-[#D4AF37]/20 bg-[#020C1B]">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-5 flex items-center justify-between">
          <BrandLockup compact />
          <Link to="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 font-sans text-[11px] uppercase tracked text-steel hover:text-[#D4AF37] transition-colors" style={{ letterSpacing: "0.24em" }}>
            <ArrowLeft size={14} /> Public Portal
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-10 py-14">
        {!authed ? (
          <div className="max-w-md mx-auto mt-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="hairline-crimson" />
              <span className="font-sans text-[11px] uppercase text-[#D4AF37]" style={{ letterSpacing: "0.32em" }}>Compliance Ledger</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl leading-[1.05] mb-4" style={{ fontWeight: 500 }}>
              Sovereign <span className="text-[#D4AF37] italic">Admin Vault.</span>
            </h1>
            <p className="font-sans text-sm text-steel mb-8 leading-relaxed">
              Provide the administrator key to access verified trade conduit submissions.
            </p>
            <form onSubmit={onUnlock} className="bg-[#020C1B] border border-[#D4AF37]/25 p-6 space-y-5" data-testid="admin-login-form">
              <label className="block">
                <span className="font-sans text-[10px] uppercase tracked text-[#D4AF37] block mb-2" style={{ letterSpacing: "0.28em" }}>Administrator Key</span>
                <div className="relative">
                  <input
                    type="password"
                    value={key}
                    onChange={(e) => setKey(e.target.value)}
                    className={inputClass + " pl-11"}
                    placeholder="Enter secure key"
                    data-testid="admin-key-input"
                    autoComplete="off"
                  />
                  <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
                </div>
              </label>
              <button
                type="submit"
                disabled={loading}
                data-testid="admin-unlock-btn"
                className="btn-crimson w-full inline-flex items-center justify-center gap-3 px-6 py-4 font-sans text-[12px] font-semibold uppercase tracked disabled:opacity-60"
                style={{ letterSpacing: "0.24em", minHeight: 52 }}
              >
                {loading ? "Verifying…" : "Unlock Ledger"}
              </button>
            </form>
          </div>
        ) : (
          <div>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="hairline-crimson" />
                  <span className="font-sans text-[11px] uppercase text-[#D4AF37]" style={{ letterSpacing: "0.32em" }}>Compliance Ledger</span>
                </div>
                <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05]" style={{ fontWeight: 500 }}>
                  Trade Conduit <span className="text-[#D4AF37] italic">Submissions.</span>
                </h1>
                <p className="font-sans text-sm text-steel mt-3">
                  {items.length} verified {items.length === 1 ? "entry" : "entries"} on record.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => load(key)}
                  disabled={loading}
                  data-testid="admin-refresh-btn"
                  className="btn-gold-outline inline-flex items-center gap-2 px-5 py-3 font-sans text-[11px] uppercase tracked"
                  style={{ letterSpacing: "0.24em", minHeight: 44 }}
                >
                  <RefreshCcw size={14} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
                <button
                  onClick={exportCsv}
                  disabled={loading || items.length === 0}
                  data-testid="admin-export-csv-btn"
                  className="btn-crimson inline-flex items-center gap-2 px-5 py-3 font-sans text-[11px] uppercase tracked disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ letterSpacing: "0.24em", minHeight: 44 }}
                >
                  <Download size={14} /> Export CSV
                </button>
              </div>
            </div>

            {/* Date-range filter panel */}
            <div className="bg-[#020C1B] border border-[#D4AF37]/20 p-5 mb-8" data-testid="admin-filter-panel">
              <div className="flex items-center gap-2 mb-4">
                <Filter size={14} className="text-[#D4AF37]" />
                <span className="font-sans text-[10px] uppercase tracked text-[#D4AF37]" style={{ letterSpacing: "0.28em" }}>
                  Date Range Filter
                </span>
              </div>
              <div className="flex flex-col md:flex-row md:items-end gap-4">
                <label className="flex-1">
                  <span className="font-sans text-[10px] uppercase tracked text-steel block mb-2" style={{ letterSpacing: "0.24em" }}>From</span>
                  <input
                    type="date"
                    value={start}
                    onChange={(e) => setStart(e.target.value)}
                    className={dateClass + " w-full"}
                    data-testid="admin-filter-start"
                  />
                </label>
                <label className="flex-1">
                  <span className="font-sans text-[10px] uppercase tracked text-steel block mb-2" style={{ letterSpacing: "0.24em" }}>To</span>
                  <input
                    type="date"
                    value={end}
                    onChange={(e) => setEnd(e.target.value)}
                    className={dateClass + " w-full"}
                    data-testid="admin-filter-end"
                  />
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={applyFilter}
                    data-testid="admin-filter-apply"
                    className="btn-gold-outline inline-flex items-center gap-2 px-5 py-2 font-sans text-[11px] uppercase tracked"
                    style={{ letterSpacing: "0.24em", minHeight: 40 }}
                  >
                    Apply
                  </button>
                  {(start || end) && (
                    <button
                      onClick={clearFilter}
                      data-testid="admin-filter-clear"
                      className="inline-flex items-center gap-2 px-4 py-2 font-sans text-[11px] uppercase tracked text-steel hover:text-[#D4AF37] border border-transparent hover:border-[#D4AF37]/40 transition-colors"
                      style={{ letterSpacing: "0.24em", minHeight: 40 }}
                    >
                      <X size={12} /> Clear
                    </button>
                  )}
                </div>
              </div>
            </div>

            {items.length === 0 ? (
              <div className="border border-[#D4AF37]/20 bg-[#020C1B] p-12 text-center" data-testid="admin-empty">
                <div className="font-serif text-2xl text-steel">No submissions in range.</div>
                <p className="font-sans text-sm text-steel/70 mt-2">Trade conduit requests matching your filter will appear here.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="admin-submissions-grid">
                {items.map((it) => (
                  <article key={it.id} data-testid={`submission-${it.id}`} className="card-vault p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-sans text-[10px] uppercase tracked text-[#D4AF37]" style={{ letterSpacing: "0.28em" }}>
                        ID · {it.id.slice(0, 8)}
                      </span>
                      <span className="inline-flex items-center gap-2 font-sans text-[10px] text-steel">
                        <Clock size={12} />
                        {new Date(it.submitted_at).toLocaleString()}
                      </span>
                    </div>
                    <h3 className="font-serif text-2xl text-bone leading-tight mb-4" style={{ fontWeight: 500 }}>
                      {it.company_name}
                    </h3>
                    <div className="space-y-3 text-sm font-sans">
                      <Row icon={FileText} label="License / Reg No." value={it.license_no} />
                      <Row icon={Mail} label="Procurement Email" value={it.procurement_email} link={`mailto:${it.procurement_email}`} />
                      <Row icon={Boxes} label="Product Interest" value={it.product_interest} accent />
                      <Row icon={Gauge} label="Monthly Volume" value={it.monthly_volume} />
                      <Row icon={Building2} label="Status" value={it.status} accent />
                    </div>

                    <div className="mt-5 pt-5 border-t border-[#D4AF37]/15 flex items-center justify-between">
                      <span className="font-sans text-[10px] uppercase tracked text-steel" style={{ letterSpacing: "0.26em" }}>
                        Compliance Certificate · {locale.toUpperCase()}
                      </span>
                      <button
                        onClick={() => downloadCertificate(it)}
                        data-testid={`cert-btn-${it.id}`}
                        className="btn-gold-outline inline-flex items-center gap-2 px-4 py-2 font-sans text-[10px] uppercase tracked"
                        style={{ letterSpacing: "0.24em", minHeight: 36 }}
                      >
                        <FileDown size={12} /> PDF
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

const Row = ({ icon: Icon, label, value, link, accent }) => (
  <div className="flex items-start gap-3">
    <Icon size={14} className="text-[#D4AF37] mt-1 shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="text-[10px] uppercase tracked text-steel" style={{ letterSpacing: "0.24em" }}>{label}</div>
      {link ? (
        <a href={link} className={`text-[15px] mt-0.5 block break-all ${accent ? "text-[#D4AF37]" : "text-bone"} hover:underline`}>{value}</a>
      ) : (
        <div className={`text-[15px] mt-0.5 break-words ${accent ? "text-[#D4AF37]" : "text-bone"}`}>{value}</div>
      )}
    </div>
  </div>
);
